/**
 * TakeoverTitleHeader — "what this frame is" plus the way to dismiss it.
 *
 * The second row of a full-frame takeover, under `WorkflowCenterBar` (whose
 * "← Back" is the takeover's exit): a title, a line of subtext saying what the
 * Servicer is expected to do or when the run started, and Close on the right.
 * Extracted from `InstanceDetailDrawer`'s inline header so the run drawer and
 * the "pick a workflow" takeover are the same chrome rather than two
 * imitations of it.
 *
 * `rightSlot` exists because the drawer puts status pills and the comfort
 * badge beside its Close; a takeover with nothing to say there passes nothing.
 * Close is optional for the same reason — a surface whose only exit is the
 * brand bar's back control simply omits it.
 *
 * `inline` (story 28633) folds the subtitle onto the title's line —
 * "Mortgagee Change · Started 5 min ago" — with slimmer padding, so the header
 * costs one line of the pane instead of two. When space runs out the subtitle
 * truncates first, then the title. Default (stacked) is unchanged.
 *
 * Shared — no Office.js, no Remix import.
 */
import type { ReactNode } from "react";
import CustomTooltip from "~/common/components/CustomTooltip";
import { TYPE_BODY, TYPE_TITLE } from "~/common/workflows/paneTypography";

export interface TakeoverTitleHeaderProps {
  title: string;
  /** One line on what to do here — "Configure inputs and launch". */
  subtitle?: ReactNode;
  /** Badges/pills rendered left of Close. */
  rightSlot?: ReactNode;
  /** Omitted → no Close button (the back control is then the only exit). */
  onClose?: () => void;
  /** Appended to Close's tooltip, e.g. "(Esc)" — the shortcut is real but
   *  invisible, so the host that has it says so. */
  closeHint?: string;
  /** One line: `title · subtitle`, py-2. Omitted → the stacked two-line look. */
  inline?: boolean;
}

export function TakeoverTitleHeader({
  title,
  subtitle,
  rightSlot,
  onClose,
  closeHint,
  inline = false,
}: TakeoverTitleHeaderProps) {
  // Tooltips are ora's CustomTooltip, not native titles (story 28633).
  const close = onClose ? (
    <CustomTooltip
      id="takeover-title-close"
      title={closeHint ? `Close ${closeHint}` : "Close"}
      placement="bottom-end"
      arrow
      isOnModal
    >
      <button
        type="button"
        onClick={onClose}
        className="touch-target rounded border border-primary-gray-light-300 bg-base-white px-3 py-1 text-xs font-medium text-primary-gray-light-600 hover:bg-primary-gray-light-50 transition-colors duration-150"
        aria-label="Close"
      >
        Close
      </button>
    </CustomTooltip>
  ) : null;

  if (inline) {
    return (
      <header
        className="flex flex-shrink-0 items-center justify-between gap-2 border-b border-primary-gray-light-200 px-4 py-2"
        data-testid="takeover-title-header"
      >
        <div className="flex min-w-0 flex-1 items-baseline gap-1">
          {/* The full title when it truncates (an empty one never opens). */}
          <CustomTooltip
            id="takeover-title"
            title={title}
            placement="bottom-start"
            arrow
            isOnModal
          >
            <h2 className={`${TYPE_TITLE} min-w-[4rem] truncate text-primary-gray-light-900`}>
              {title}
            </h2>
          </CustomTooltip>
          {subtitle !== undefined && (
            <p
              // Shrinks (and truncates) long before the title does: on a
              // narrow terminal run the pills keep their room and the
              // workflow's name survives; the start time gives way first.
              className={`${TYPE_BODY} min-w-0 shrink-[999] truncate whitespace-nowrap text-primary-gray-light-500`}
              data-testid="takeover-title-subtitle"
            >
              <span aria-hidden="true">· </span>
              {subtitle}
            </p>
          )}
        </div>
        {(rightSlot || close) && (
          <div className="flex flex-shrink-0 items-center gap-2">
            {rightSlot}
            {close}
          </div>
        )}
      </header>
    );
  }

  return (
    <header className="flex flex-shrink-0 items-center justify-between px-4 py-3 border-b border-primary-gray-light-200">
      <div className="flex flex-col min-w-0">
        <h2 className="text-md font-semibold text-primary-gray-light-900 truncate">
          {title}
        </h2>
        {subtitle !== undefined && (
          <p className="text-2xs text-primary-gray-light-500">{subtitle}</p>
        )}
      </div>
      <div className="flex items-center gap-2">
        {rightSlot}
        {close}
      </div>
    </header>
  );
}

export default TakeoverTitleHeader;
